import { useEffect, useState } from "react";
import { Download, Smartphone, ExternalLink, Share2, Info } from "lucide-react";

/**
 * PWA install prompt.
 * - On Chromium (Android desktop/Chrome): captures `beforeinstallprompt` and shows a big install button.
 * - On iOS Safari: renders manual "Add to Home Screen" instructions (Apple doesn't expose the event).
 * - On Firefox/other: falls back to instructions.
 */
export default function InstallAppButton({ testid = "install-app-btn", compact = false }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    setIsIOS(ios);
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setInstalled(standalone);

    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (installed) {
    return (
      <div
        data-testid={`${testid}-installed`}
        className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5"
      >
        <Smartphone size={12} /> Installed as app
      </div>
    );
  }

  const clickInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else if (isIOS) {
      setShowIOS(true);
    } else {
      setShowIOS(true);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={testid}
        onClick={clickInstall}
        className={`inline-flex items-center gap-2 font-semibold rounded-md transition-colors ${
          compact
            ? "text-xs bg-primary text-primary-foreground hover:bg-primary/90 px-3 py-1.5"
            : "text-sm bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-5 shadow-brut-orange"
        }`}
      >
        <Download size={compact ? 12 : 16} /> Install Mailroom App
      </button>

      {showIOS && (
        <div className="fixed inset-0 z-50 bg-foreground/60 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIOS(false)} data-testid="install-help-modal" role="dialog" aria-labelledby="install-help-title" aria-describedby="install-help-desc">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Smartphone size={18} className="text-primary" />
              <h3 id="install-help-title" className="font-display text-xl">Install Mailroom on your phone</h3>
            </div>
            <p id="install-help-desc" className="sr-only">Step-by-step instructions to install Mailroom OS as an app on iOS or Android, and a link to package it as a signed APK/IPA via PWA Builder.</p>

            {isIOS ? (
              <ol className="text-sm space-y-3 text-foreground">
                <li className="flex gap-2"><span className="font-bold text-primary">1.</span> Tap the <b className="inline-flex items-center gap-1"><Share2 size={14}/> Share</b> button at the bottom of Safari.</li>
                <li className="flex gap-2"><span className="font-bold text-primary">2.</span> Scroll and tap <b>"Add to Home Screen"</b>.</li>
                <li className="flex gap-2"><span className="font-bold text-primary">3.</span> Tap <b>Add</b>. The Mailroom icon appears on your home screen and opens like a native app.</li>
              </ol>
            ) : (
              <ol className="text-sm space-y-3 text-foreground">
                <li className="flex gap-2"><span className="font-bold text-primary">1.</span> Open <b>Chrome</b> on your Android device and visit this page.</li>
                <li className="flex gap-2"><span className="font-bold text-primary">2.</span> Tap the <b>⋮ menu</b> (top-right) then choose <b>"Install app"</b> or <b>"Add to Home screen"</b>.</li>
                <li className="flex gap-2"><span className="font-bold text-primary">3.</span> Confirm. Mailroom installs as a native-feeling app with camera + push access.</li>
              </ol>
            )}

            <div className="bg-red-50 border border-primary/30 rounded-md p-3 text-xs text-foreground flex gap-2">
              <Info size={14} className="text-primary shrink-0 mt-0.5" />
              <div>
                <b>Need a store-signed APK / IPA?</b> Use <a href="https://www.pwabuilder.com/" target="_blank" rel="noreferrer" className="text-primary font-semibold underline inline-flex items-center gap-1">PWA Builder <ExternalLink size={10}/></a> — paste this app's URL and download a signed Play-store-ready package. Same for iOS.
              </div>
            </div>

            <div className="flex justify-end">
              <button
                data-testid="install-help-close"
                onClick={() => setShowIOS(false)}
                className="text-sm font-semibold text-primary hover:underline"
              >Got it</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
